// src/pages/ReportingPage.tsx
import React, { useState } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, AreaChart, Area, SankeyChart,
} from "recharts";
import {
  Download, Filter, Calendar, TrendingUp, Award, BarChart2,
  Zap, Tv, Smartphone, Users, Globe, ArrowRight,
} from "lucide-react";
import { clsx } from "clsx";
import { CAMPAIGNS, INCREMENTALITY_RESULTS, ATTRIBUTION_PATHS } from "@/data";
import { identityGraphStats } from "@/data/identityGraph";

type ReportTab = "performance" | "attribution" | "incrementality" | "ctv" | "crossdevice" | "efficiency";

const WEEKLY_PERF = [
  { week: "W1",  spend: 920,  conversions: 8400,  roas: 5.8 },
  { week: "W2",  spend: 1040, conversions: 9200,  roas: 6.1 },
  { week: "W3",  spend: 980,  conversions: 8800,  roas: 5.9 },
  { week: "W4",  spend: 1120, conversions: 10400, roas: 6.4 },
  { week: "W5",  spend: 1240, conversions: 11800, roas: 6.8 },
  { week: "W6",  spend: 1180, conversions: 11200, roas: 6.6 },
  { week: "W7",  spend: 1320, conversions: 13100, roas: 7.1 },
  { week: "W8",  spend: 1280, conversions: 12600, roas: 6.9 },
];

const CHANNEL_ATTR = [
  { channel: "CTV",     firstTouch: 28, linear: 22, dataDriven: 18, lastTouch: 8  },
  { channel: "Display", firstTouch: 24, linear: 28, dataDriven: 31, lastTouch: 22 },
  { channel: "O&O",     firstTouch: 18, linear: 26, dataDriven: 32, lastTouch: 48 },
  { channel: "Video",   firstTouch: 20, linear: 16, dataDriven: 12, lastTouch: 14 },
  { channel: "Direct",  firstTouch: 10, linear: 8,  dataDriven: 7,  lastTouch: 8  },
];

const SUPPRESSION_DATA = [
  { week: "W1", saved: 42, total: 280 },
  { week: "W2", saved: 48, total: 310 },
  { week: "W3", saved: 51, total: 298 },
  { week: "W4", saved: 58, total: 342 },
  { week: "W5", saved: 64, total: 380 },
  { week: "W6", saved: 61, total: 365 },
  { week: "W7", saved: 72, total: 410 },
  { week: "W8", saved: 68, total: 428 },
];

// ── CTV performance data ──────────────────────────────────────
const CTV_WEEKLY = [
  { week: "W1", householdReach: 1.2, completionRate: 84, conversions: 1240, spend: 180 },
  { week: "W2", householdReach: 1.4, completionRate: 86, conversions: 1480, spend: 210 },
  { week: "W3", householdReach: 1.3, completionRate: 83, conversions: 1320, spend: 195 },
  { week: "W4", householdReach: 1.6, completionRate: 87, conversions: 1720, spend: 240 },
  { week: "W5", householdReach: 1.8, completionRate: 88, conversions: 1940, spend: 270 },
  { week: "W6", householdReach: 1.7, completionRate: 86, conversions: 1820, spend: 255 },
  { week: "W7", householdReach: 2.0, completionRate: 89, conversions: 2180, spend: 300 },
  { week: "W8", householdReach: 1.9, completionRate: 88, conversions: 2060, spend: 285 },
];

// ── Cross-device attribution paths ────────────────────────────
const CROSS_DEVICE_PATHS = [
  { path: "CTV → Mobile Display → Desktop Purchase",   conversions: 4812, revenue: 481200, pct: 37.5, devices: 3 },
  { path: "CTV → Desktop Display → Desktop Purchase",  conversions: 3204, revenue: 320400, pct: 24.9, devices: 2 },
  { path: "Mobile Display → Desktop Purchase",          conversions: 2568, revenue: 256800, pct: 20.0, devices: 2 },
  { path: "CTV → O&O → Mobile Purchase",               conversions: 1284, revenue: 128400, pct: 10.0, devices: 3 },
  { path: "Desktop Only",                               conversions:  972, revenue:  97200, pct:  7.6, devices: 1 },
];

// ── Attribution funnel ────────────────────────────────────────
const FUNNEL_DATA = [
  { name: "Impressions",  value: 473_000_000, fill: "#1E2761" },
  { name: "Reach",        value:  91_400_000, fill: "#3056D3" },
  { name: "Engaged",      value:  12_800_000, fill: "#0891B2" },
  { name: "Site Visits",  value:   2_840_000, fill: "#06b6d4" },
  { name: "Conversions",  value:     284_000, fill: "#10b981" },
];

const ATTR_COLORS = ["#1E2761","#3056D3","#0891B2","#10b981"];

function fmt(n: number): string {
  if (n >= 1_000_000_000) return `${(n/1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `${(n/1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

export default function ReportingPage() {
  const [tab, setTab]           = useState<ReportTab>("performance");
  const [attrModel, setAttrModel] = useState("dataDriven");
  const [dateRange, setDateRange] = useState("last30");

  const activeCampaigns = CAMPAIGNS.filter(c => c.status === "active");
  const totalSpend      = activeCampaigns.reduce((s, c) => s + c.spent, 0);
  const totalConv       = activeCampaigns.reduce((s, c) => s + c.conversions, 0);
  const totalImpr       = activeCampaigns.reduce((s, c) => s + c.impressions, 0);
  const avgRoas         = activeCampaigns.filter(c => c.roas > 0).reduce((s,c,_,a) => s + c.roas/a.length, 0);
  const suppSavings     = 642_600;

  // CTV summary stats
  const ctvTotalHouseholds = CTV_WEEKLY.reduce((s,w) => s + w.householdReach, 0);
  const ctvAvgCompletion   = CTV_WEEKLY.reduce((s,w) => s + w.completionRate, 0) / CTV_WEEKLY.length;
  const ctvTotalConversions= CTV_WEEKLY.reduce((s,w) => s + w.conversions, 0);
  const ctvTotalSpend      = CTV_WEEKLY.reduce((s,w) => s + w.spend, 0);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Reporting Cockpit</h1>
          <p className="page-subtitle">Attribution, incrementality, CTV performance, and cross-device analytics</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <select className="input pr-8 text-xs appearance-none cursor-pointer" value={dateRange} onChange={e => setDateRange(e.target.value)}>
              <option value="last7">Last 7 days</option>
              <option value="last30">Last 30 days</option>
              <option value="last90">Last 90 days</option>
              <option value="mtd">Month to date</option>
            </select>
            <Calendar size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
          </div>
          <button className="btn-secondary btn-sm flex items-center gap-1.5"><Filter size={12} /> Filter</button>
          <button className="btn-primary btn-sm flex items-center gap-1.5"><Download size={12} /> Export</button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { label: "Total Spend",        value: `$${(totalSpend/1_000_000).toFixed(1)}M`,  delta: 14.3, icon: BarChart2,   color: "bg-blue/10 text-blue"          },
          { label: "Total Impressions",  value: `${(totalImpr/1_000_000).toFixed(0)}M`,    delta: 9.8,  icon: TrendingUp,  color: "bg-cyan/10 text-cyan"          },
          { label: "Total Conversions",  value: totalConv.toLocaleString(),                 delta: 18.2, icon: Award,       color: "bg-emerald-50 text-emerald-600"},
          { label: "Avg ROAS",           value: `${avgRoas.toFixed(1)}x`,                   delta: 6.2,  icon: Zap,         color: "bg-navy/10 text-navy"          },
          { label: "Suppression Savings",value: `$${(suppSavings/1000).toFixed(0)}K`,       delta: 18.4, icon: TrendingUp,  color: "bg-blue/10 text-blue"          },
        ].map((kpi, i) => (
          <div key={i} className="kpi-card">
            <div className="flex items-center justify-between">
              <p className="kpi-label">{kpi.label}</p>
              <div className={clsx("w-7 h-7 rounded-lg flex items-center justify-center", kpi.color)}>
                <kpi.icon size={13} />
              </div>
            </div>
            <p className="kpi-value mt-1">{kpi.value}</p>
            <div className="flex items-center gap-1 mt-1">
              <TrendingUp size={11} className="text-emerald-500" />
              <span className="kpi-delta-up">+{kpi.delta}% vs prior period</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {([
          { id: "performance",    label: "Campaign Performance" },
          { id: "attribution",    label: "Attribution"          },
          { id: "incrementality", label: "Incrementality"       },
          { id: "ctv",            label: "CTV Performance"      },
          { id: "crossdevice",    label: "Cross-Device"         },
          { id: "efficiency",     label: "Efficiency"           },
        ] as { id: ReportTab; label: string }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={clsx("px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-150 -mb-px whitespace-nowrap",
              tab === t.id ? "border-blue text-blue" : "border-transparent text-slate hover:text-navy")}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Campaign Performance ── */}
      {tab === "performance" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-1">Weekly Spend vs Conversions</h3>
              <p className="text-xs text-slate mb-3">All active campaigns — last 8 weeks</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={WEEKLY_PERF} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(v: any, n: any) => [n === "spend" ? `$${v}K` : Number(v).toLocaleString(), n === "spend" ? "Spend ($K)" : "Conversions"]} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="spend"       fill="#3056D3" radius={[3,3,0,0]} name="spend" />
                  <Bar dataKey="conversions" fill="#0891B2" radius={[3,3,0,0]} name="conversions" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-1">ROAS Trend</h3>
              <p className="text-xs text-slate mb-3">Data-driven attribution — last 8 weeks</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={WEEKLY_PERF} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} domain={[4, 8]} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(v: any) => [`${Number(v).toFixed(1)}x`, "ROAS"]} />
                  <Line type="monotone" dataKey="roas" stroke="#1E2761" strokeWidth={2.5} dot={{ fill: "#1E2761", r: 4 }} name="ROAS" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-navy">Campaign Performance Detail</h3>
              <button className="btn-secondary btn-sm flex items-center gap-1.5"><Download size={11} /> Export CSV</button>
            </div>
            <table className="table-base">
              <thead><tr><th>Campaign</th><th>Impressions</th><th>Clicks</th><th>CTR</th><th>Conversions</th><th>CPA</th><th>ROAS</th><th>Spend</th></tr></thead>
              <tbody>
                {activeCampaigns.map(c => {
                  const ctr = c.impressions > 0 ? (c.clicks/c.impressions*100) : 0;
                  const cpa = c.conversions > 0 ? (c.spent/c.conversions) : 0;
                  return (
                    <tr key={c.id}>
                      <td><p className="font-medium text-gray-800 max-w-[200px] truncate">{c.name}</p><p className="text-xs text-slate">{c.advertiser}</p></td>
                      <td className="tabular">{(c.impressions/1_000_000).toFixed(1)}M</td>
                      <td className="tabular">{(c.clicks/1_000).toFixed(0)}K</td>
                      <td className="tabular">{ctr.toFixed(2)}%</td>
                      <td className="tabular">{c.conversions.toLocaleString()}</td>
                      <td className="tabular">${cpa.toFixed(2)}</td>
                      <td><span className={clsx("font-semibold tabular", c.roas >= 5 ? "text-emerald-600" : "text-amber-600")}>{c.roas.toFixed(1)}x</span></td>
                      <td className="tabular">${(c.spent/1000).toFixed(0)}K</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Attribution ── */}
      {tab === "attribution" && (
        <div className="space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-navy">Attribution Model Comparison</h3>
              <div className="flex gap-1">
                {[
                  { value: "dataDriven", label: "Data-Driven" },
                  { value: "linear",     label: "Linear"      },
                  { value: "lastTouch",  label: "Last Touch"  },
                  { value: "firstTouch", label: "First Touch" },
                ].map(m => (
                  <button key={m.value} onClick={() => setAttrModel(m.value)}
                    className={clsx("btn btn-sm", attrModel === m.value ? "btn-primary" : "btn-secondary")}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={CHANNEL_ATTR} margin={{ top: 4, right: 4, left: -16, bottom: 0 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} unit="%" />
                <YAxis type="category" dataKey="channel" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={52} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }} formatter={(v: any) => [`${v}%`, "Credit"]} />
                <Bar dataKey={attrModel} radius={[0,4,4,0]} name="Attribution Credit">
                  {CHANNEL_ATTR.map((_, i) => <Bar key={i} dataKey={attrModel} fill={ATTR_COLORS[i % ATTR_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Conversion Funnel</h3>
              <div className="space-y-2">
                {FUNNEL_DATA.map((stage, i) => {
                  const pct     = i === 0 ? 100 : (stage.value / FUNNEL_DATA[0].value * 100);
                  const dropoff = i > 0 ? ((FUNNEL_DATA[i-1].value - stage.value) / FUNNEL_DATA[i-1].value * 100) : 0;
                  return (
                    <div key={i}>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs font-medium text-gray-800">{stage.name}</span>
                        <div className="flex gap-3">
                          {i > 0 && <span className="text-xs text-red-400">-{dropoff.toFixed(1)}%</span>}
                          <span className="text-xs font-semibold text-navy tabular">
                            {stage.value >= 1_000_000 ? `${(stage.value/1_000_000).toFixed(1)}M` : `${(stage.value/1_000).toFixed(0)}K`}
                          </span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-3">
                        <div className="h-3 rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: stage.fill }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Top Conversion Paths</h3>
              <p className="text-xs text-slate mb-3">DeWalt Q2 — data-driven model</p>
              <div className="space-y-3">
                {ATTRIBUTION_PATHS[0].paths.map((path, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-1 mb-1 flex-wrap">
                        {path.sequence.map((ch, j) => (
                          <React.Fragment key={j}>
                            <span className="badge badge-slate text-2xs">{ch}</span>
                            {j < path.sequence.length - 1 && <span className="text-gray-300 text-xs">›</span>}
                          </React.Fragment>
                        ))}
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="bg-blue h-1.5 rounded-full" style={{ width: `${path.pct}%` }} />
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-semibold text-navy tabular">{path.pct}%</p>
                      <p className="text-2xs text-slate">{path.conversions.toLocaleString()} conv</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Incrementality ── */}
      {tab === "incrementality" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Avg Measured Lift",   value: "201%",  sub: "Across all tested campaigns",  color: "text-emerald-600" },
              { label: "Avg Confidence",      value: "97.1%", sub: "Statistical significance",      color: "text-blue"        },
              { label: "Incremental Revenue", value: "$11.7M",sub: "Attributed to media exposure",  color: "text-navy"        },
            ].map((m,i) => (
              <div key={i} className="kpi-card">
                <p className="kpi-label">{m.label}</p>
                <p className={clsx("text-2xl font-bold tabular mt-1", m.color)}>{m.value}</p>
                <p className="text-xs text-slate mt-0.5">{m.sub}</p>
              </div>
            ))}
          </div>
          {INCREMENTALITY_RESULTS.map(result => (
            <div key={result.campaignId} className="card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-navy">{result.campaignName}</h3>
                  <p className="text-xs text-slate mt-0.5">{result.holdoutPct}% holdout · Ghost bid methodology</p>
                </div>
                <div className="flex gap-3">
                  <div className="text-center bg-emerald-50 rounded-lg px-3 py-2">
                    <p className="text-lg font-bold text-emerald-600 tabular">+{result.lift.toFixed(0)}%</p>
                    <p className="text-2xs text-slate">Lift</p>
                  </div>
                  <div className="text-center bg-blue/5 rounded-lg px-3 py-2">
                    <p className="text-lg font-bold text-blue tabular">{result.confidenceLevel}%</p>
                    <p className="text-2xs text-slate">Confidence</p>
                  </div>
                  <div className="text-center bg-navy/5 rounded-lg px-3 py-2">
                    <p className="text-lg font-bold text-navy tabular">${(result.incrementalRevenue/1_000_000).toFixed(1)}M</p>
                    <p className="text-2xs text-slate">Incr. Revenue</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate mb-2">Conversion Rate Comparison</p>
                  <div className="space-y-2">
                    {[
                      { label: "Exposed Group", value: result.exposedConvRate, color: "bg-emerald-500", textColor: "text-emerald-600" },
                      { label: "Control Group", value: result.controlConvRate,  color: "bg-gray-400",   textColor: "text-slate"       },
                    ].map((g,i) => (
                      <div key={i}>
                        <div className="flex justify-between mb-1">
                          <span className="text-xs font-medium text-gray-800">{g.label}</span>
                          <span className={clsx("text-xs font-semibold tabular", g.textColor)}>{g.value}%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                          <div className={clsx("h-2.5 rounded-full", g.color)} style={{ width: `${g.value * 10}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  {[
                    { label: "Incremental Conversions", value: result.incrementalConversions.toLocaleString() },
                    { label: "Incremental Revenue",     value: "$" + (result.incrementalRevenue/1_000_000).toFixed(1) + "M" },
                    { label: "Cost per Incr. Conv",     value: "$" + (result.incrementalRevenue/result.incrementalConversions/10).toFixed(2) },
                  ].map((m,i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-xs text-slate">{m.label}</span>
                      <span className="text-xs font-semibold text-navy tabular">{m.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── CTV Performance ── */}
      {tab === "ctv" && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: "Household Reach",     value: `${ctvTotalHouseholds.toFixed(1)}M`,      sub: "Unique HHs reached via CTV",        icon: Tv,    color: "bg-navy/10 text-navy"          },
              { label: "Avg Completion Rate", value: `${ctvAvgCompletion.toFixed(1)}%`,         sub: "Video ad completions",               icon: Zap,   color: "bg-blue/10 text-blue"          },
              { label: "CTV Conversions",     value: ctvTotalConversions.toLocaleString(),       sub: "Person-level attributed",           icon: Award, color: "bg-emerald-50 text-emerald-600"},
              { label: "CTV Spend",           value: `$${(ctvTotalSpend/1000).toFixed(0)}K`,    sub: "8-week total",                      icon: BarChart2,color:"bg-cyan/10 text-cyan"        },
            ].map((kpi,i) => (
              <div key={i} className="kpi-card">
                <div className="flex items-center justify-between">
                  <p className="kpi-label">{kpi.label}</p>
                  <div className={clsx("w-7 h-7 rounded-lg flex items-center justify-center", kpi.color)}>
                    <kpi.icon size={13} />
                  </div>
                </div>
                <p className="kpi-value mt-1">{kpi.value}</p>
                <p className="text-xs text-slate mt-0.5">{kpi.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-1">Household Reach & Conversions</h3>
              <p className="text-xs text-slate mb-3">Weekly CTV performance — person-level attribution</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={CTV_WEEKLY} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(v: any, n: any) => [n === "householdReach" ? `${v}M HHs` : Number(v).toLocaleString(), n === "householdReach" ? "HH Reach" : "Conversions"]} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="householdReach" fill="#1E2761" radius={[3,3,0,0]} name="householdReach" />
                  <Bar dataKey="conversions"    fill="#0891B2" radius={[3,3,0,0]} name="conversions"    />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-1">Video Completion Rate</h3>
              <p className="text-xs text-slate mb-3">Weekly completion % — industry avg: 72%</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={CTV_WEEKLY} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} domain={[70, 95]} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }} formatter={(v: any) => [`${v}%`, "Completion Rate"]} />
                  <Line type="monotone" dataKey="completionRate" stroke="#1E2761" strokeWidth={2.5} dot={{ fill: "#1E2761", r: 4 }} name="Completion Rate" />
                  {/* Industry benchmark line */}
                  <Line type="monotone" dataKey={() => 72} stroke="#e5e7eb" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Industry Avg (72%)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* CTV identity advantage */}
          <div className="card bg-navy/5 border border-navy/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-navy/10 flex items-center justify-center">
                <Tv size={16} className="text-navy" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-navy">Person-Level CTV Attribution</h3>
                <p className="text-xs text-slate">MRN links {fmt(identityGraphStats.ctv.linkedToIdentity)} CTV IFAs to verified person identities</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "CTV IFAs Linked",      value: fmt(identityGraphStats.ctv.linkedToIdentity), sub: "to person identity"          },
                { label: "IFA Match Rate",        value: `${(identityGraphStats.ctv.matchRate * 100).toFixed(1)}%`, sub: "of total IFAs" },
                { label: "Cross-Screen Attribution",value: "100%", sub: "CTV to purchase linked"                                         },
                { label: "Avg Screens/HH",        value: "1.4x",  sub: "CTV devices per household"                                       },
              ].map((m,i) => (
                <div key={i} className="bg-white rounded-lg p-3 border border-navy/10 text-center">
                  <p className="text-sm font-bold text-navy tabular">{m.value}</p>
                  <p className="text-2xs text-slate mt-0.5">{m.label}</p>
                  <p className="text-2xs text-slate/60">{m.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Cross-Device ── */}
      {tab === "crossdevice" && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: "Unique Persons Reached", value: "48.2M",  sub: "Across all channels"               },
              { label: "Avg Devices/Person",     value: "2.8x",   sub: "Mobile + CTV + desktop"            },
              { label: "Cross-Device Conversions",value: "68.4%", sub: "Crossed 2+ devices before purchase" },
              { label: "CTV-Assisted Conversions",value: "41.2%", sub: "CTV exposure in path"              },
            ].map((kpi,i) => (
              <div key={i} className="kpi-card">
                <p className="kpi-label">{kpi.label}</p>
                <p className="kpi-value">{kpi.value}</p>
                <p className="text-xs text-slate mt-0.5">{kpi.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Cross-Device Conversion Paths</h3>
              <p className="text-xs text-slate mb-3">How consumers cross devices before converting</p>
              <div className="space-y-3">
                {CROSS_DEVICE_PATHS.map((path, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="flex gap-0.5 flex-shrink-0">
                          {Array.from({ length: path.devices }).map((_, j) => (
                            <div key={j} className={clsx("w-3 h-3 rounded-sm", j === 0 ? "bg-navy" : j === 1 ? "bg-blue" : "bg-cyan")} />
                          ))}
                        </div>
                        <span className="text-xs text-gray-700 truncate">{path.path}</span>
                      </div>
                      <span className="text-xs font-semibold text-navy tabular flex-shrink-0 ml-2">{path.pct}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="bg-gradient-to-r from-navy to-blue h-1.5 rounded-full" style={{ width: `${path.pct * 2}%` }} />
                    </div>
                    <div className="flex justify-between mt-0.5">
                      <span className="text-2xs text-slate">{path.conversions.toLocaleString()} conversions</span>
                      <span className="text-2xs text-slate">${(path.revenue/1_000).toFixed(0)}K revenue</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="card">
                <h3 className="text-sm font-semibold text-navy mb-3">Channel Role in Cross-Device Journey</h3>
                <div className="space-y-2">
                  {[
                    { channel: "CTV",          role: "Awareness initiator",  pct: 84, color: "bg-navy"  },
                    { channel: "Display",      role: "Mid-funnel retarget",  pct: 91, color: "bg-blue"  },
                    { channel: "O&O",          role: "Conversion driver",    pct: 78, color: "bg-cyan"  },
                    { channel: "Mobile",       role: "Last-touch reminder",  pct: 62, color: "bg-blue"  },
                  ].map((item,i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs font-medium text-gray-800 w-16 flex-shrink-0">{item.channel}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-2">
                        <div className={clsx("h-2 rounded-full", item.color)} style={{ width: `${item.pct}%` }} />
                      </div>
                      <span className="text-xs text-slate w-32 flex-shrink-0">{item.role}</span>
                      <span className="text-xs font-semibold text-navy tabular w-8 flex-shrink-0">{item.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card bg-cyan/5 border border-cyan/20">
                <div className="flex items-center gap-2 mb-2">
                  <Zap size={14} className="text-cyan" />
                  <p className="text-xs font-semibold text-navy">Cross-Device Identity Advantage</p>
                </div>
                <p className="text-xs text-slate leading-relaxed mb-2">
                  MRN's person-level identity graph links CTV, mobile, and desktop exposures to the same individual — eliminating duplicate counting and enabling true cross-device attribution.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Person IDs",    value: fmt(identityGraphStats.individuals)          },
                    { label: "CTV linked",    value: fmt(identityGraphStats.ctv.linkedToIdentity)  },
                    { label: "MAIDs linked",  value: fmt(identityGraphStats.mobileDevices.total)   },
                    { label: "IPs linked",    value: fmt(identityGraphStats.ipAddresses.total)     },
                  ].map((m,i) => (
                    <div key={i} className="bg-white rounded-lg p-2 text-center border border-cyan/20">
                      <p className="text-xs font-bold text-navy tabular">{m.value}</p>
                      <p className="text-2xs text-slate">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Efficiency ── */}
      {tab === "efficiency" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Suppression Savings MTD", value: "$642.6K", sub: "Waste eliminated", delta: 18.4 },
              { label: "Frequency Waste Avoided", value: "$218K",   sub: "Over-exposure prevented", delta: 12.1 },
              { label: "Match Rate",              value: "91%",     sub: "Avg across partners", delta: 1.2  },
            ].map((m,i) => (
              <div key={i} className="kpi-card">
                <p className="kpi-label">{m.label}</p>
                <p className="text-2xl font-bold text-navy tabular mt-1">{m.value}</p>
                <p className="text-xs text-slate mt-0.5">{m.sub}</p>
                <div className="flex items-center gap-1 mt-1.5">
                  <TrendingUp size={11} className="text-emerald-500" />
                  <span className="kpi-delta-up">+{m.delta}% vs last month</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-1">Weekly Suppression Savings</h3>
              <p className="text-xs text-slate mb-3">Spend saved vs total spend ($K)</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={SUPPRESSION_DATA} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }}
                    formatter={(v: any, n: any) => [`$${v}K`, n === "saved" ? "Saved" : "Total Spend"]} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total" fill="#e5e7eb" radius={[3,3,0,0]} name="total" />
                  <Bar dataKey="saved" fill="#0891B2" radius={[3,3,0,0]} name="saved" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Efficiency Scorecard</h3>
              <div className="space-y-3">
                {[
                  { label: "Suppression rate",          value: "15.9%", benchmark: "Industry avg: 0%",    status: "good"    },
                  { label: "Identity match rate",        value: "91%",   benchmark: "Industry avg: 25%",   status: "good"    },
                  { label: "Avg frequency (per person)", value: "4.2x",  benchmark: "Cap: 5x/week",        status: "good"    },
                  { label: "Data-driven attribution",    value: "78%",   benchmark: "Of campaigns",        status: "good"    },
                  { label: "Holdout testing coverage",   value: "64%",   benchmark: "Target: 80%+",        status: "warning" },
                  { label: "CTV person-level match",     value: "74.6%", benchmark: "Of CTV IFAs linked",  status: "good"    },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                    <div>
                      <p className="text-xs font-medium text-gray-800">{item.label}</p>
                      <p className="text-2xs text-slate">{item.benchmark}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-navy tabular">{item.value}</span>
                      <span className={clsx("w-2 h-2 rounded-full", item.status === "good" ? "bg-emerald-500" : "bg-amber-400")} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}